import React, { useContext } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { formatTimeAMPM } from "../../../Utils/timeUtils";
import * as route from "../../../routes";
import {
  ACTION_APPROVED,
  ACTION_REJECTED,
  PENDING_L1_APPROVAL,
  PENDING_L2_APPROVAL,
} from "../constants";
import { UserContext } from "../../../Context";
import { ROLES } from "../../../Constants/Strings";

const MobileAttendanceCard = ({
  data,
  displayActionButtons = false,
  handleApproval,
  setActiveRejectRow,
}) => {
  const navigate = useNavigate();
  const {
    site_name,
    date,
    checkin_time,
    checkout_time,
    is_backdated,
    user_name,
    status,
  } = data;

  const { user } = useContext(UserContext);

  const canShowActions =
    (user?.role === ROLES.PROJECT_ENGINEER && status === PENDING_L1_APPROVAL) ||
    (user?.role === ROLES.PROJECT_MANAGER && status === PENDING_L2_APPROVAL);

  const getStatusBadgeClass = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "bg-green-50 border border-green-200 text-green-700";
      case "rejected":
        return "bg-red-50 border border-red-200 text-red-700";
      case "pending":
      case "pending_l1_approval":
      case "pending_l2_approval":
        return "bg-yellow-50 border border-yellow-200 text-yellow-700";
      case "in_progress":
        return "bg-blue-50 border border-blue-200 text-blue-700";
      case "closed":
        return "bg-gray-50 border border-gray-200 text-gray-700";
      default:
        return "bg-gray-50 border border-gray-200 text-gray-600";
    }
  };

  return (
    <>
      <div
        className="flex justify-between items-start p-5 rounded-xl bg-white border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer mb-3"
        onClick={() =>
          navigate(route.VIEW_DETAILED_PAGE.replace(":id", data.id))
        }
      >
        {/* Left section: site, user, date, time */}
        <div className="flex flex-col space-y-2 truncate max-w-[70%]">
          <span className="font-semibold text-gray-800 truncate">
            {site_name}
          </span>
          <span className="font-medium text-gray-600 truncate">
            {user_name}
          </span>

          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{date}</span>
            <span>
              <span className="text-primary font-medium">
                {formatTimeAMPM(checkin_time)}
              </span>{" "}
              -{" "}
              <span className="text-red-600 font-medium">
                {formatTimeAMPM(checkout_time)}
              </span>
            </span>
          </div>

          <div className="flex items-center gap-2 mt-1">
            {is_backdated === "Yes" && (
              <span className="text-gray-600 font-medium rounded-full bg-gray-100 px-2 py-1 text-xs">
                Backdated
              </span>
            )}

            <span
              className={`text-xs font-medium px-3 py-1 rounded-full truncate ${getStatusBadgeClass(
                status
              )}`}
            >
              {status
                ?.toLowerCase()
                .split("_")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" ") || "N/A"}
            </span>
          </div>
        </div>

        {/* Right section: action buttons */}
        {displayActionButtons && canShowActions && (
          <div className="flex flex-col justify-between items-center gap-2">
            <button
              className={`p-2 rounded-full ${
                status?.toLowerCase() === "approved" ||
                status?.toLowerCase() === "rejected"
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-green-600 hover:bg-green-50 transition-colors"
              }`}
              disabled={
                status?.toLowerCase() === "approved" ||
                status?.toLowerCase() === "rejected"
              }
              onClick={(e) => {
                e.stopPropagation();
                handleApproval(data.id, ACTION_APPROVED);
              }}
            >
              <FaCheck size={22} />
            </button>
            <button
              className={`p-2 rounded-full ${
                status?.toLowerCase() === "approved" ||
                status?.toLowerCase() === "rejected"
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-500 hover:bg-red-50 transition-colors"
              }`}
              disabled={
                status?.toLowerCase() === "approved" ||
                status?.toLowerCase() === "rejected"
              }
              onClick={(e) => {
                e.stopPropagation();
                handleApproval(data.id, ACTION_REJECTED);
                setActiveRejectRow(data);
              }}
            >
              <FaTimes size={22} />
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default MobileAttendanceCard;
